'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as generateUUID } from 'uuid';
import { DateTime } from 'luxon';
import { http } from '../lib/api/http';
import { V2, V3, V4 } from '../lib/api/urls';
import { User } from '../models/profile';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../lib/api/browserStorage';

type LoginSignature = (email: string, password: string) => Promise<boolean>;
type LogoutSignature = () => Promise<boolean>;
type LoginVKSignature = () => Promise<void>;

const AuthorizationContext = createContext<{
  isReady: boolean;
  isAuthenticated: boolean | null;
  login?: LoginSignature;
  logout?: LogoutSignature;
  getUser: () => User | null;
  fetchUser?: () => void;
  onLoginVK?: LoginVKSignature;
  user: User | null;
}>({ isReady: false, isAuthenticated: false, getUser: () => null, user: null });

export function AuthorizationProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const getUser = () => user;

  const fetchUser = async () => {
    const userData: User = await http.get(V4.user, null, null, {
      contentType: 'json',
      withTokenRequest: true,
    });
    if (userData.phone_number) {
      userData.phone_number = `+${userData.phone_number}`;
    }
    setUser(userData);
  };
  const onLoginVK = async () => {
    const localStorageUser = getLocalStorageItem('current-user');
    const parsedUser = localStorageUser ? JSON.parse(localStorageUser) : null;
    if (parsedUser && parsedUser.token) {
      await fetchUser();
      setIsAuthenticated(true);
      setIsReady(true);
    }
  };

  const login: (
    username: string,
    password: string,
    isAnonUser?: boolean
  ) => Promise<any> = async (username, password, isAnonUser) =>
    http
      .post(V2.token, { username, password })
      .then((response) => {
        if (response.status === 401) {
          throw response.data;
        }
        if (response.status === 200) {
          const localStorageUser = getLocalStorageItem('current-user');
          const parsedUser = localStorageUser
            ? JSON.parse(localStorageUser)
            : null;
          setLocalStorageItem(
            'current-user',
            JSON.stringify({
              [isAnonUser ? 'uuid' : 'email']: username,
              token: response.data.access,
              refresh: response.data.refresh,
            })
          );
          if (parsedUser && parsedUser.uuid && !isAnonUser) {
            http.post(
              V2.userMerge,
              { anon_user_uuid: parsedUser.uuid },
              undefined,
              {
                contentType: 'json',
                withTokenRequest: true,
              }
            );
          }
          fetchUser().then(() => {
            setIsAuthenticated(!isAnonUser);
            setIsReady(true);
            http.post(V3.statistic, { platform: 'WEB' }, undefined, {
              contentType: 'json',
              withTokenRequest: true,
            });
          });
        }
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  const createAnonUser = async () => {
    const uuid = generateUUID();
    const response = await http.post(V3.register, { anon_uuid: uuid });

    await login(uuid, response.data.password, true);

    setIsAuthenticated(false);
    setIsReady(true);
  };

  const logout: LogoutSignature = async () => {
    localStorage.removeItem('current-user');
    setUser(null);
    await createAnonUser();
    return true;
  };
  useEffect(() => {
    const localStorageUser = getLocalStorageItem('current-user');
    const parsedUser = localStorageUser ? JSON.parse(localStorageUser) : null;
    if (parsedUser?.token) {
      fetchUser()
        .then(() => {
          setIsAuthenticated(!parsedUser.uuid);
          setIsReady(true);
        })
        .catch((err) => {
          if (err.statusCode === 401) {
            http
              .post(V2.tokenRefresh, {
                refresh: parsedUser.refresh,
              })
              .then((response) => {
                if (response.status === 401) {
                  logout();
                }
                if (response.status === 200) {
                  setLocalStorageItem(
                    'current-user',
                    JSON.stringify({
                      [parsedUser.email ? 'email' : 'uuid']: parsedUser.email
                        ? parsedUser.email
                        : parsedUser.uuid,
                      token: response.data.access,
                      refresh: response.data.refresh,
                    })
                  );
                  fetchUser()
                    .then(() => {
                      setIsAuthenticated(!parsedUser.uuid);
                      setIsReady(true);
                    })
                    .catch(() => {
                      localStorage.removeItem('current-user');
                      createAnonUser();
                    });
                }
                return response.data;
              })
              .catch((error) => {
                throw error;
              });
          }
        });
    } else {
      createAnonUser();
    }
  }, []);

  useEffect(() => {
    if (isReady) {
      let dateLastEntry = null;
      if (getLocalStorageItem('date-last-entry')) {
        dateLastEntry = getLocalStorageItem('date-last-entry');
      } else {
        dateLastEntry = DateTime.now().toISO();
        if (dateLastEntry) {
          http.post(V3.statistic, { platform: 'WEB' }, undefined, {
            contentType: 'json',
            withTokenRequest: true,
          });
          setLocalStorageItem('date-last-entry', dateLastEntry);
          return;
        }
      }
      if (getLocalStorageItem('date-last-entry') && dateLastEntry) {
        const diffDays = DateTime.now().diff(
          DateTime.fromISO(dateLastEntry),
          'days'
        ).days;
        if (diffDays && diffDays > 1) {
          http.post(V3.statistic, { platform: 'WEB' }, undefined, {
            contentType: 'json',
            withTokenRequest: true,
          });
          const newDateLastEntry = DateTime.now().toISO();
          if (newDateLastEntry) {
            setLocalStorageItem('date-last-entry', newDateLastEntry);
          }
        }
      }
    }
  }, [isReady]);

  const contextValue = useMemo(
    () => ({
      isReady,
      isAuthenticated,
      login,
      logout,
      getUser,
      fetchUser,
      onLoginVK,
      user,
    }),
    [getUser]
  );
  return (
    <AuthorizationContext.Provider value={contextValue}>
      {children}
    </AuthorizationContext.Provider>
  );
}

export function useAuthorization() {
  return useContext(AuthorizationContext);
}
